import { FormControl, FormHelperText, InputBase, InputLabel, MenuItem, Select, styled } from "@mui/material";
import { getTranslatedCertificateTypeString } from "lib/helper/getTranslatedRowDataStrings";
import React, { useEffect, useState, useMemo } from "react";
import ordinaryColors from "themes/ordinaryColors";
import Props from "./index.d";
import useGetForeignKeyList from "./useGetForeignKeyList";
import { LabelArray, getSortedOptions } from "lib/helper/getSortedOptions";
import { atom, useSetRecoilState, useRecoilValue } from "recoil";
import { showNoteOnCarefulInputConditionally } from "pages/UploadCertificate/utils";
import { getCertificateCreateFieldSelector } from "raft/CertificateForm";

export const selectedCertificateTypeAtom = atom<boolean>({
  key: "selectedCertificateTypeAtom",
  default: false,
});

export const StyledInputBase = styled(InputBase)<{ error?: boolean; isEnumSelector?: boolean }>(
  ({ error, isEnumSelector }) => ({
    maxHeight: "42px",
    minHeight: "42px",
    display: "flex",
    padding: "8px 12px",
    alignContent: "center",
    rowGap: "100px",
    flex: "1 0 0",
    flexWrap: "wrap",
    borderRadius: "0px",
    border: "1px solid ",
    borderColor: error ? ordinaryColors.error : ordinaryColors.border,
    backgroundColor: error ? "rgba(211, 47, 47, 0.02)" : "transparent",
    marginBottom: "0px",
  })
);

// Herstellererklärung (Manufacturer's Declaration) - UUID
const HERSTELLERERKLARUNG_ID = "a980cca4-ca1b-5954-9d95-d784a5008571";

const ForeignKeySelectorCertificateTypes: React.FC<Props> = (props) => {
  const {
    data: { state, setstate, updateValue, onBlur },
    title,
    pickDefaultFirst,
    className,
    foreignKeyParams,
    hideLabel,
    errorManager,
    isRequiredField,
    isUnit,
    disabled,
    testId,
  } = props;
  const elemsPre = useGetForeignKeyList({ foreignKeyParams });
  const setSelectedCertificateTypeAtom = useSetRecoilState(selectedCertificateTypeAtom);
  const isEdit = Object.keys(props.raftVariables).includes("id");
  const [showNoteOnCarefulInput, setShowNoteOnCarefulInput] = useState(false);

  // Detect if we're in the grid forming form by checking the matrix form's certificateFormType
  // Regular form (active) doesn't set this field, so it remains undefined
  const certificateFormTypeMatrixSelector = useMemo(
    () => getCertificateCreateFieldSelector({ formVersion: "matrixCertificateCreate" })("certificateFormType"),
    []
  );
  const certificateFormTypeMatrix = useRecoilValue(certificateFormTypeMatrixSelector);
  const isGridForming = certificateFormTypeMatrix === "GRID_FORMING";

  // Filter out Herstellererklärung from grid-forming forms completely
  const filteredElemsPre = useMemo(() => {
    if (!isGridForming || foreignKeyParams.entityName !== "certificateTypes") {
      return elemsPre;
    }
    return elemsPre.filter((elem) => elem.value !== HERSTELLERERKLARUNG_ID);
  }, [elemsPre, isGridForming, foreignKeyParams.entityName]);

  useEffect(() => {
    if (pickDefaultFirst && !state && !!elemsPre && elemsPre.length) {
      setstate(elemsPre[0].value);
    }
  }, [elemsPre, state, pickDefaultFirst, setstate]);

  const errorManagerRes = errorManager({
    errorJoiner: (errors) => {
      return errors.join(", ");
    },
  });

  const elems =
    foreignKeyParams.entityName === "certificateTypes"
      ? (getSortedOptions({ labelArray: filteredElemsPre, sortFn: getTranslatedCertificateTypeString }) as LabelArray)
      : filteredElemsPre;

  useEffect(() => {
    if (!state) return;
    setSelectedCertificateTypeAtom(true);
  }, [setSelectedCertificateTypeAtom, state]);
  return (
    <>
      <FormControl fullWidth className={className}>
        {!isUnit || (hideLabel && <InputLabel id="demo-simple-select-label">{title}</InputLabel>)}
        {showNoteOnCarefulInputConditionally({
          condition: showNoteOnCarefulInput,
        })}
        <Select
          data-testid={testId}
          disabled={isEdit || disabled}
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          input={<StyledInputBase {...errorManagerRes} />}
          value={state ?? ""}
          label={hideLabel === true ? undefined : title}
          onBlur={() => {
            setShowNoteOnCarefulInput(false);
            onBlur();
          }}
          onChange={updateValue}
          onFocus={() => setShowNoteOnCarefulInput(true)}
          required={isRequiredField}
          {...errorManagerRes}
        >
          {elems.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {foreignKeyParams.entityName !== "certificateTypes" ? label : getTranslatedCertificateTypeString(value)}
            </MenuItem>
          ))}
        </Select>
        {errorManagerRes.error && !isUnit && (
          <FormHelperText sx={{ color: ordinaryColors.error, marginLeft: "0px", fontSize: "12px" }}>
            {errorManagerRes.helperText}
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
};

export default ForeignKeySelectorCertificateTypes;
